<script setup lang="ts">
import { Input } from '~/components/ui/input'

const INN_LENGTH = 14

interface IProps {
  modelValue?: string | number
  errors?: number
}

const props = defineProps<IProps>()

const emits = defineEmits<{
  (e: 'update:modelValue', payload: string): void
}>()

const inputRef = ref<InstanceType<typeof Input> | null>(null)

const inputValue = computed(() => (props.modelValue === undefined ? '' : String(props.modelValue)))

const allowedKeys = [
  'Backspace',
  'Delete',
  'Tab',
  'Escape',
  'Enter',
  'Home',
  'End',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown'
]

const sanitize = (value: string) => value.replace(/\D/g, '').slice(0, INN_LENGTH)

const handleKeydown = (event: KeyboardEvent) => {
  if (event.ctrlKey || event.metaKey) return
  if (allowedKeys.includes(event.key)) return

  if (!/^\d$/.test(event.key)) {
    event.preventDefault()
    return
  }

  const target = event.target as HTMLInputElement
  const hasSelection = target.selectionStart !== target.selectionEnd

  if (inputValue.value.length >= INN_LENGTH && !hasSelection) {
    event.preventDefault()
  }
}

const handleInput = (value: string | number) => {
  const rawValue = String(value)
  const cleanValue = sanitize(rawValue)

  // Paste and autofill bypass keydown, so the DOM node keeps the unsanitized
  // text until it is rewritten explicitly
  if (cleanValue !== rawValue) {
    nextTick(() => {
      const el = inputRef.value?.inputRef
      if (el) el.value = cleanValue
    })
  }

  emits('update:modelValue', cleanValue)
}
</script>

<template>
  <Input
    ref="inputRef"
    :model-value="inputValue"
    type="text"
    inputmode="numeric"
    autocomplete="off"
    :maxlength="INN_LENGTH"
    :aria-invalid="Boolean(props.errors)"
    @keydown="handleKeydown"
    @update:model-value="handleInput"
  />
</template>
